import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import edit from '@amzn/meridian-tokens/base/icon/edit';
import trash from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import { TableCell, TableRow } from '@amzn/meridian/table';
import Table, { type TableSortDirection } from '@amzn/meridian/table/table';
import Text from '@amzn/meridian/text';
import HoverPopover from 'src/components/shared/hoverPopover/hoverPopover';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import type { Modification, ModificationWithRegionsAndTasksDTO } from 'src/services/modification/types';
import {
  resetRestrictionFormTableFilter,
  setRestrictionFormTableFilterProp,
} from 'src/store/adminConfigPageSlice/regionTasksPageSlice/regionTasksPageSlice';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

import { COLUMNS } from '../constants';

// **** Constants **** //

const WORK_REQUIREMENT_FORM_PATH = '/adminConfiguration/restrictionFormConfiguration/forms';

// **** Types **** //

interface JobModificationTableProps {
  tableData: ModificationWithRegionsAndTasksDTO[];
  searchField: string;
  onEdit: (data: Modification) => void;
  onDelete: (data: Modification) => void;
}

interface OnSortProps {
  sortColumn: keyof Modification;
  sortDirection: TableSortDirection;
}

interface AssignedToContentProps {
  row: ModificationWithRegionsAndTasksDTO;
  onClick: (row: ModificationWithRegionsAndTasksDTO) => void;
}

// **** Components **** //

/**
 * Default component
 */
const JobModificationTable = ({ tableData, searchField, onEdit, onDelete }: JobModificationTableProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const [sortColumn, setSortColumn] = useState<keyof Modification>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const [filteredAndSortedTableData, setFilteredAndSortedTableData] = useState(tableData);
  const [perPage, setPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  // Filter, sorting, and pagination
  useEffect(() => {
    if (!tableData.length) return;
    const { paginatedData, totalPages: newTotalPages } = filterSortAndPaginateTableData(
      tableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id', 'regions'],
      perPage,
    );
    setFilteredAndSortedTableData(paginatedData);
    setTotalPages(newTotalPages);
  }, [tableData, searchField, sortColumn, sortDirection, currentPage, perPage]);

  // Go back to first page when filter/sort
  useEffect(() => {
    setCurrentPage(1);
  }, [perPage, searchField, sortColumn, sortDirection]);

  // "onSort" function
  const onSort = useCallback(({ sortColumn, sortDirection }: OnSortProps) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  // When clicking on the "Assigned To" column
  const onClickAssignedTo = (modification: ModificationWithRegionsAndTasksDTO) => {
    const regionNames = modification.regions.map((region) => region.name);
    if (regionNames.length > 0) {
      dispatch(resetRestrictionFormTableFilter());
      dispatch(setRestrictionFormTableFilterProp({ stateRegions: regionNames }));
    }
    return navigate(WORK_REQUIREMENT_FORM_PATH, { replace: true });
  };

  return (
    <>
      {/* Table */}
      <Box type="outline">
        <Table
          headerRows={1}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          showDividers={true}
          fixHeaderRows={true}
          rowComponents={[UncontrolledExpandableRow, TableRow]}
        >
          {/* Header */}
          <TableRow>
            {COLUMNS.map(({ sortKey, name, width }, idx) => (
              <TableCell width={width} key={`${idx}-${sortKey}`} sortColumn={sortKey}>
                {name}
              </TableCell>
            ))}
          </TableRow>

          {/* Body */}
          {filteredAndSortedTableData.map((row) => (
            <UncontrolledExpandableRow key={row.id}>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.isFreeFormTextAllowed ? 'yes' : 'no'}</TableCell>
              <TableCell>
                <AssignedToContent row={row} onClick={onClickAssignedTo} />
              </TableCell>
              <TableCell>
                <Button type="icon" aria-label={`Edit ${row.name}`} onClick={() => onEdit(row)}>
                  <Icon tokens={edit} />
                </Button>
              </TableCell>
              <TableCell>
                <Button type="icon" aria-label={`Delete ${row.name}`} onClick={() => onDelete(row)}>
                  <Icon tokens={trash} />
                </Button>
              </TableCell>
            </UncontrolledExpandableRow>
          ))}
        </Table>
      </Box>

      {/* Pagination */}
      {!!totalPages && (
        <PaginationWithDropdown
          totalPages={totalPages}
          currentPage={currentPage}
          perPage={perPage}
          setCurrentPage={setCurrentPage}
          setPerPage={setPerPage}
        />
      )}
    </>
  );
};

/**
 * Get the content for the "Assigned To" column.
 */
const AssignedToContent = ({ row, onClick }: AssignedToContentProps): JSX.Element =>
  row.regions.length > 0 ? (
    <HoverPopover title={<AssignedToPopoverTitle row={row} />} popoverProps={{ maxWidth: 400 }}>
      <Link onClick={() => onClick(row)}>
        {row.regions.length === 1 ? row.regions[0].name : `${row.regions.length} regions`}
      </Link>
    </HoverPopover>
  ) : (
    <></>
  );

/**
 * Get the Tooltip Title for the assigned to Column.
 * Note: not using merdian <Table/> component because it's being deprecated:
 *  https://meridian.a2z.com/components/table/?platform=react-web#h4-table-legacy
 * Didn't use <DataTable/> because it applied a bunch of styling I didn't need.
 */
const AssignedToPopoverTitle = ({ row }: { row: ModificationWithRegionsAndTasksDTO }) => (
  <Box>
    <Text tag="ul">
      {row.regions.map((region) => (
        <React.Fragment key={region.id}>
          <li>{region.name}</li>
          {region.tasks.length > 0 && (
            <ul>
              <li>
                <Text tag="span" type="b200">
                  {region.tasks.join(', ')}
                </Text>
              </li>
            </ul>
          )}
        </React.Fragment>
      ))}
    </Text>
  </Box>
);

// **** Export **** //

export default JobModificationTable;
